import { useEffect, useRef, useState } from 'react';
import { env } from '@/lib/env';
import { getAccessToken } from './client';

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface UseWebSocketOptions<T> {
  /** Path beginning with `/ws/...` — the JWT is appended via `?token=`. */
  path: string | null;
  onMessage?: (data: T) => void;
  /** Send these messages on (re)connect. Useful for store filters. */
  onOpenSend?: unknown[];
  /** Disable auto-reconnect (default: enabled with exponential backoff). */
  noReconnect?: boolean;
}

/**
 * Reusable typed WebSocket hook.
 *
 * Backend WS auth (per `core.ws_auth.py`) accepts JWT via:
 *   - `?token=` query string (used here — browsers can't set Authorization on upgrade)
 *   - `Sec-WebSocket-Protocol`
 *
 * Reconnect: exponential backoff capped at 15s; resets on a clean OPEN.
 */
export function useWebSocket<T = unknown>({ path, onMessage, onOpenSend, noReconnect }: UseWebSocketOptions<T>) {
  const [status, setStatus] = useState<WsStatus>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const stoppedRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!path) {
      setStatus('idle');
      return;
    }
    stoppedRef.current = false;

    const connect = () => {
      const token = getAccessToken();
      if (!token) {
        setStatus('closed');
        return;
      }
      const url = `${env.wsBase}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      setStatus('connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retriesRef.current = 0;
        setStatus('open');
        onOpenSend?.forEach((m) => ws.send(typeof m === 'string' ? m : JSON.stringify(m)));
      };
      ws.onmessage = (evt) => {
        try {
          const parsed = JSON.parse(evt.data) as T;
          onMessageRef.current?.(parsed);
        } catch {
          // Some Channels consumers send plain strings — pass through as unknown.
          onMessageRef.current?.(evt.data as unknown as T);
        }
      };
      ws.onerror = () => setStatus('error');
      ws.onclose = () => {
        setStatus('closed');
        if (stoppedRef.current || noReconnect) return;
        const delay = Math.min(15_000, 500 * Math.pow(2, retriesRef.current));
        retriesRef.current += 1;
        window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stoppedRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, noReconnect]);

  return { status, send: (m: unknown) => wsRef.current?.send(typeof m === 'string' ? m : JSON.stringify(m)) };
}
